import { zodResolver } from "@hookform/resolvers/zod";
import { FieldValues, FormProvider, Resolver, SubmitHandler, useForm, UseFormProps, UseFormReturn } from "react-hook-form";
import { z, ZodType } from "zod";
import { cn } from "@/utils/classes";

/* Prop Types */
interface FormProps<TFormValues extends FieldValues, Schema> {
  onSubmit: SubmitHandler<TFormValues>;
  schema: Schema;
  className?: string;
  children: (methods: UseFormReturn<TFormValues>) => React.ReactNode;
  options?: UseFormProps<TFormValues>;
  id?: string;
}

/* 
  Form component using react-hook-form and zod for validation
*/
export const Form = <Schema extends ZodType<FieldValues, FieldValues>, TFormValues extends FieldValues = z.infer<Schema>>({
  onSubmit,
  children,
  className,
  options,
  id,
  schema,
}: FormProps<TFormValues, Schema>) => {
  // TFormValues defaults to z.infer<Schema>, but TypeScript can't prove that link while it is a
  // free type parameter, so the resolver is asserted onto the form's value type
  const form = useForm({ ...options, resolver: zodResolver(schema) as unknown as Resolver<TFormValues> });
  return (
    <FormProvider {...form}>
      <form className={cn(className)} onSubmit={form.handleSubmit(onSubmit)} id={id}>
        {children(form)}
      </form>
    </FormProvider>
  );
};
